/**
 * /settings pure redirect stubs (RO-3-09 / #44302).
 *
 * Ports of excellent/app/settings page.tsx stubs whose entire body is a
 * next/navigation redirect(). Same recipe as agent-redirects.tsx: a plain
 * string destination, thrown from beforeLoad as a typed redirect({to,replace}).
 * Destinations match the Next originals exactly — including those that land
 * outside /settings (/goals, /agents).
 *
 * Live Next stubs under excellent/app/settings are UNTOUCHED (D6).
 */

import { createRoute, redirect } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { VerificationShell } from "../verification/shell";
import { SettingsLanding } from "../verification/settings-landing";

function go(to: string) {
  return () => {
    // Widened to `string` — some destinations (e.g. `/goals`) are not yet in
    // this tree's typed-path union; the route-tree would refuse a literal.
    const dest: string = to;
    throw redirect({ to: dest, replace: true });
  };
}

/**
 * WP9a / #46619 — settings landing inside the verification shell.
 * Tools-menu Settings opens this page. #46666 wraps /settings/* leaves in
 * VerificationShell via RootShell (this landing already wraps; do not nest).
 * Not a redirect to /settings/user (that remains for /settings/user itself).
 */
function SettingsLandingPage() {
  return (
    <div data-route="settings-landing">
      <VerificationShell>
        <SettingsLanding />
      </VerificationShell>
    </div>
  );
}

export const settingsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsLandingPage,
});

/** Port of `settings/user/page.tsx` → `/settings/user/profile`. */
export const settingsUserIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/user",
  beforeLoad: go("/settings/user/profile"),
});

/**
 * Port of `settings/sections/page.tsx` → workspace sections.
 * #44576: `/settings/workspace/sections` is unmounted (#44574). Interim:
 * mounted `/settings/workspace` (real page). Restore the sections leaf when
 * that sibling lands.
 */
export const settingsSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/sections",
  beforeLoad: go("/settings/workspace"),
});

/**
 * Port of `settings/repos/page.tsx` → agents repos.
 * #44573: real `/settings/agents/repos` is mounted — restore Next destination
 * (was interim-retargeted by #44576 while the leaf was unmounted).
 */
export const settingsReposRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/repos",
  beforeLoad: go("/settings/agents/repos"),
});

/**
 * Port of `settings/integrations/connections/page.tsx`.
 * #44576: `/settings/integrations` is unmounted (#44574). Interim: workspace
 * settings hub (mounted).
 */
export const settingsIntegrationsConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/connections",
  beforeLoad: go("/settings/workspace"),
});

/**
 * Port of `settings/integrations/catalog/page.tsx`.
 * #44576: same interim as connections — mounted workspace settings.
 */
export const settingsIntegrationsCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations/catalog",
  beforeLoad: go("/settings/workspace"),
});

/** Port of `settings/customers/page.tsx`. */
export const settingsCustomersIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/customers",
  beforeLoad: go("/settings/customers/pipelines"),
});

/**
 * Port of `settings/auto-verify/page.tsx`.
 * #44573: real `/settings/agents/auto-verify` is mounted — restore Next
 * destination (was interim-retargeted by #44576 while the leaf was unmounted).
 */
export const settingsAutoVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/auto-verify",
  beforeLoad: go("/settings/agents/auto-verify"),
});

/** Port of `settings/workspace/identity/page.tsx`. */
export const settingsWorkspaceIdentityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/workspace/identity",
  beforeLoad: go("/settings/workspace"),
});

/** Port of `settings/agents/goals/page.tsx`. */
export const settingsAgentsGoalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/agents/goals",
  beforeLoad: go("/goals"),
});

/**
 * Port of `settings/agents/workers/page.tsx`.
 * Leftover hop: `/agents` remounts ProductChrome. Land on `/`.
 */
export const settingsAgentsWorkersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/agents/workers",
  beforeLoad: go("/"),
});
